import type { Table } from '@tanstack/react-table'

import { X } from 'lucide-react'
import { Button } from '~/shadcn/ui/button/button'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import {
  getProjectLoanPurposeOptions,
  interestRateOptions,
  type FilterOptionValues,
} from '~/@mid_ui/table/tableFilterOptions'
import { MultiSelect } from '~/@pages_investing/projects/_projects_filtering/MultiSelect'
import type { ProjectCollateralSubcategory } from '~/@pages_investing/projects/_project_types/projectCollateralSubCategory'
import { CollateralSubcategorySelect } from '~/@pages_investing/projects/_projects_filtering/CollateralSubcategorySelect'
import type { ProjectsItem } from '~/@pages_investing/projects/_project_types/project.types'
import {
  amountTargetOptions,
  getPaymentTypeOptions,
  investmentPeriodOptions,
  ltlOptions,
  riskOptions,
} from '../_projects_filtering/projectTableFilterOptions'

type RunningProjectsFiltersProps = { table: Table<ProjectsItem> }

function RunningProjectsFilters({ table }: RunningProjectsFiltersProps) {
  const isDesktop = useIsDesktop()

  const closeButton = (
    <Button
      variant="ghost"
      className="self-baseline lg:self-center p-2"
      onClick={() => table.setColumnFilters([])}>
      <X className="w-5 h-5" />
      <span className="ml-1 text-black-900">Resetovať filtre</span>
    </Button>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row flex-wrap items-center gap-3">
        <MultiSelect
          label="Účel úveru"
          options={getProjectLoanPurposeOptions()}
          values={
            table
              .getColumn('loanPurpose')!
              .getFilterValue() as FilterOptionValues<
              ReturnType<typeof getProjectLoanPurposeOptions>
            >
          }
          onChange={table.getColumn('loanPurpose')!.setFilterValue}
        />
        <CollateralSubcategorySelect
          values={
            table
              .getColumn('collateralSubcategory')!
              .getFilterValue() as Array<ProjectCollateralSubcategory>
          }
          onChange={table.getColumn('collateralSubcategory')!.setFilterValue}
        />
        <MultiSelect
          label="Úrok ako predpokladaný výnos"
          options={interestRateOptions}
          values={
            table
              .getColumn('interestRate')!
              .getFilterValue() as FilterOptionValues<
              typeof interestRateOptions
            >
          }
          onChange={table.getColumn('interestRate')!.setFilterValue}
        />

        <MultiSelect
          label="Výplata úroku"
          options={getPaymentTypeOptions()}
          values={
            table
              .getColumn('paymentTypeId')!
              .getFilterValue() as FilterOptionValues<
              ReturnType<typeof getPaymentTypeOptions>
            >
          }
          onChange={table.getColumn('paymentTypeId')!.setFilterValue}
        />
        <MultiSelect
          label="Úverový rámec"
          options={amountTargetOptions}
          values={
            table
              .getColumn('amountTarget')!
              .getFilterValue() as FilterOptionValues<
              typeof amountTargetOptions
            >
          }
          onChange={table.getColumn('amountTarget')!.setFilterValue}
        />
        <MultiSelect
          label="Doba úročenia"
          options={investmentPeriodOptions}
          values={
            table
              .getColumn('investmentPeriod')!
              .getFilterValue() as FilterOptionValues<
              typeof investmentPeriodOptions
            >
          }
          onChange={table.getColumn('investmentPeriod')!.setFilterValue}
        />
        <MultiSelect
          label="LTL"
          options={ltlOptions}
          values={
            table
              .getColumn('ltlPercentage')!
              .getFilterValue() as FilterOptionValues<typeof ltlOptions>
          }
          onChange={table.getColumn('ltlPercentage')!.setFilterValue}
        />

        <MultiSelect
          label="Riziko"
          options={riskOptions}
          values={
            table
              .getColumn('riskScore')!
              .getFilterValue() as FilterOptionValues<typeof riskOptions>
          }
          onChange={table.getColumn('riskScore')!.setFilterValue}
        />
        {isDesktop && closeButton}
      </div>
      {!isDesktop && closeButton}
    </div>
  )
}

export { RunningProjectsFilters }
